import { useLazyQuery, useMutation } from '@apollo/client';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  ADD_EXAMPLE,
  ADD_FILE_DOCUMENT,
  ADD_LINK_DOCUMENT,
} from '../../../graphql/mutations';
import AggregateErrors from '../../../utils/AggregateErrors';
import axios from 'axios';
import { ReturnLayout } from '../../../Components/ReturnLayout';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Button,
  Col,
  Row,
  Stack,
} from 'react-bootstrap';
import ExampleForm, {
  type ExampleFormData,
} from '../../../Components/ExampleForm';
import { LIST_ALL_DOCUMENTS } from '../../../graphql/queries';
import FloatingTextInput from '../../../Components/FloatingTextInput';
import FloatingComboBoxInput from '../../../Components/FloatingComboBoxInput';
import { debounce } from 'lodash';
import { type FileDocument, type LinkDocument } from '../../../__generated__/graphql';
import SavingStatus from '../../../Components/SavingStatus';

/**
 * Ce composant permet d'afficher le formulaire nécessaire pour
 * l'ajout d'un exemple associé à un indicateur.
 *
 * @returns Le composant rendu par React
 */
export default function AddExample(): React.ReactElement {
  // <!-- Hooks GraphQL -->

  useEffect(() => {
    fetchDocuments({
      variables: {
        skip: 0,
        take: 6,
        where: null,
      },
    });
  }, []);

  const [
    fetchDocuments,
    { data: documentsData, error: documentsError, loading: documentsLoading },
  ] = useLazyQuery(LIST_ALL_DOCUMENTS);

  const debouncer = useMemo(
    () =>
      debounce((value: string) => {
        fetchDocuments({
          variables: {
            skip: 0,
            take: 20,
            where: value ? { title: { contains: value } } : null,
          },
        });
      }, 300),
    []
  );

  const [
    addFileDocument,
    {
      data: addFileDocumentData,
      error: addFileDocumentError,
      loading: addFileDocumentLoading,
    },
  ] = useMutation(ADD_FILE_DOCUMENT);
  const [
    addLinkDocument,
    {
      data: addLinkDocumentData,
      error: addLinkDocumentError,
      loading: addLinkDocumentLoading,
    },
  ] = useMutation(ADD_LINK_DOCUMENT);
  const [
    addExample,
    {
      data: addExampleData,
      error: addExampleError,
      loading: addExampleLoading,
    },
  ] = useMutation(ADD_EXAMPLE);

  // <!-- Hooks de React -->
  const navigate = useNavigate();
  const [uploadDocumentLoading, setUploadDocumentLoading] = useState(false);
  const [uploadDocumentLoadingError, setUploadDocumentError] = useState('');
  const [isComplete, setComplete] = useState(false);
  const [selectedDocument, setSelectedDocument] = useState('');
  const [search, setSearch] = useState('');
  const [mode, setMode] = useState<'existing' | 'new'>('existing');
  const [remark, setRemark] = useState('');

  // <!-- Constantes d'erreurs GraphQL -->
  const addFileDocumentErrors = AggregateErrors(
    addFileDocumentError,
    addFileDocumentData?.addFileDocument.errors
  );
  const addLinkDocumentErrors = AggregateErrors(
    addLinkDocumentError,
    addLinkDocumentData?.addLinkDocument.errors
  );
  const addExampleErrors = AggregateErrors(
    addExampleError,
    addExampleData?.addExample.errors
  );
  const errors = addFileDocumentErrors.concat(
    ...addLinkDocumentErrors,
    ...addExampleErrors
  );
  if (uploadDocumentLoadingError !== '') {
    errors.push(uploadDocumentLoadingError);
  }

  // <!-- Autres variables -->
  const { id } = useParams();

  const filteredDocuments =
    documentsData?.documents?.items?.filter(d => d !== null) ?? [];

  const comboBoxOptions = useMemo(
    () =>
      filteredDocuments
        .filter((d): d is FileDocument | LinkDocument => !!d)
        .map(document => ({
          id: document.id,
          label: document.title,
        })),
    [filteredDocuments]
  );

  const loading = 
    addFileDocumentLoading ||
    addLinkDocumentLoading ||
    addExampleLoading ||
    uploadDocumentLoading;

  const combinedErrors = [...errors];
  if(documentsError) {
    combinedErrors.push(documentsError.message);
  }

  // <!-- Autres fonctions -->
  const handleSearchChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
      setSearch(e.target.value);
      setSelectedDocument('');
      debouncer(e.target.value);
    }, [debouncer]);

  const handleSelectDocument = useCallback((d: FileDocument | LinkDocument | null) => {
      setSearch(d?.title ?? '');
      setSelectedDocument(d?.id ?? '');
    }, []);

  const handleSelectExisting = useCallback(() => {
    setMode('existing');
  }, []);

  const handleSelectNew = useCallback(() => {
    setMode('new');
  }, []);

  const handleOptionSelect = useCallback((option: { id: string }) => {
      const document = filteredDocuments.find(d => d?.id === option.id) ?? null;
      handleSelectDocument(document);
    }, [filteredDocuments, handleSelectDocument]);

  const handleRemarkChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
      setRemark(e.target.value);
    }, []);

  const handleAddDocument = useCallback(() => {
    if (selectedDocument) {
      sendExample(selectedDocument, remark);
    }
  }, [selectedDocument, remark]);

  const sendExample = (documentId: string, remark: string): void => {
    addExample({
      variables: {
        indicatorId: id ?? '',
        documentId,
        remark,
      },
    })
      .then(addExampleResult => {
        if (
          !addExampleResult.errors &&
          !addExampleResult.data?.addExample.errors
        ) {
          navigate(`/indicateurs/${id}`);
        } else {
          setComplete(true);
        }
      })
      .finally(() => {
        setComplete(true);
      });
  };

  // <!-- Fonction de rappel des événements -->
  const onFormSubmit = useCallback(
    (
      event: React.FormEvent<HTMLFormElement>,
      data: ExampleFormData,
      setValidated: React.Dispatch<React.SetStateAction<boolean>>
    ): void => {
      if (
        !addFileDocumentLoading &&
        !addLinkDocumentLoading &&
        !addExampleLoading
      ) {
        event.preventDefault();
        event.stopPropagation();

        const authors = data.authors?.map(tmp => tmp);
        setComplete(false);
        if (
          data.title !== undefined &&
          data.title !== '' &&
          data.abstract !== undefined &&
          data.abstract !== null &&
          data.abstract !== '' &&
          data.remark !== '' &&
          data.type !== undefined &&
          (data.authors ?? []).every(a => a !== '') &&
          id !== undefined &&
          ((data.type === 'FILE' &&
            data.fileName !== undefined &&
            data.fileName !== '' &&
            data.document !== undefined &&
            data.document !== null) ||
            (data.type === 'LINK' && data.url !== undefined && data.url !== ''))
        ) {
          if (data.type === 'LINK') {
            addLinkDocument({
              variables: {
                title: data.title,
                abstract: data.abstract,
                url: data.url ?? '',
                publicationYear: data.publicationYear,
                authors,
              },
            })
              .then(result => {
                if (!result.errors && !result.data?.addLinkDocument.errors) {
                  sendExample(
                    result.data?.addLinkDocument?.document?.id ?? '',
                    data.remark ?? ''
                  );
                } else {
                  setComplete(true);
                }
              })
              .catch(() => {
                setComplete(true);
              });
          } else {
            addFileDocument({
              variables: {
                title: data.title,
                abstract: data.abstract,
                publicationYear: data.publicationYear,
                authors,
                fileName: data.fileName ?? '',
              },
            })
              .then(result => {
                if (!result.errors && !result.data?.addFileDocument.errors) {
                  setUploadDocumentLoading(true);
                  axios
                    .post(
                      result.data?.addFileDocument.uploadToken?.uploadURL ?? '',
                      data.document,
                      {
                        headers: {
                          'Content-Type': 'application/pdf',
                        },
                        withCredentials: true,
                      }
                    )
                    .then(response => {
                      if (response.data.id !== null) {
                        sendExample(response.data.id, data.remark ?? '');
                      }
                    })
                    .catch(error => {
                      if (error.response.status === 409) {
                        sendExample(
                          error.response.data.ressourceId,
                          data.remark ?? ''
                        );
                      } else if (error.response !== undefined) {
                        setUploadDocumentError(error.response.data);
                        setComplete(true);
                      } else if (error.request !== undefined) {
                        setUploadDocumentError(error.request);
                        setComplete(true);
                      } else {
                        setUploadDocumentError(error.message);
                        setComplete(true);
                      }
                    })
                    .finally(() => {
                      setUploadDocumentLoading(false);
                    });
                } else {
                  setComplete(true);
                }
              })
              .catch(() => {
                setComplete(true);
              });
          }
        } else {
          setComplete(true);
        }
        setValidated(true);
      }
    },
    [addFileDocumentLoading, addLinkDocumentLoading, addExampleLoading]
  );

  return (
    <ReturnLayout href={`/indicateurs/${id}`}>
      <Row className="justify-content-md-start">
        <Col md="10">
          <div className="h2">Ajouter un exemple</div>
        </Col>
      </Row>

      {/* Tabs */}
      <Row className="mb-3">
        <Col>
          <Stack direction="horizontal" gap={2} className="nav nav-pills">
            <Button
              variant={mode === 'existing' ? 'primary' : 'outline-primary'}
              onClick={handleSelectExisting}
              className="nav-item"
            >
              Document existant
            </Button>
            <Button
              variant={mode === 'new' ? 'primary' : 'outline-primary'}
              onClick={handleSelectNew}
              className="nav-item"
            >
              Nouveau document
            </Button>
          </Stack>
        </Col>
      </Row>

      {/* Mode document existant */}
      {mode === 'existing' && (
        <>
          <div className="mb-3">
            <FloatingComboBoxInput
              id="document-search"
              label="Chercher un document..."
              value={search}
              onChange={handleSearchChange}
              onOptionSelect={handleOptionSelect}
              options={comboBoxOptions}
              loading={documentsLoading}
              required={true}
              helpBlock="Veuillez spécifier le titre du document pour chercher ce dernier."
            />
          </div>

          {/* Champ remarque */}
          <FloatingTextInput
            id="example-remark"
            testId="example-remark"
            label="Remarques"
            value={remark}
            required
            onChange={handleRemarkChange}
            className="mb-3"
            helpBlock="Les remarques sont des commentaires que vous souhaitez ajouter à l'exemple."
            invalidFeedback="Les remarques sont obligatoires"
          />

          <Button
            onClick={handleAddDocument}
            disabled={!selectedDocument || search === ''}
          >
            Ajouter
          </Button>
        </>
      )}

      <SavingStatus
        complete={isComplete}
        errors={combinedErrors}
        loading={loading}
        />

      {/* Mode nouveau document */}
      {mode === 'new' && (
        <Row>
          <Col>
            <ExampleForm
              onFormSubmit={onFormSubmit}
              isComplete={isComplete}
              errors={errors}
              loading={
                addFileDocumentLoading ||
                addExampleLoading ||
                addLinkDocumentLoading ||
                uploadDocumentLoading
              }
              documentCanBeEdited
            />
          </Col>
        </Row>
      )}
    </ReturnLayout>
  );
}
